import React, { useEffect, useState, useMemo } from "react"
import { Modal } from "reactstrap"
import api from "API/Api"
import SweetAlert from "react-bootstrap-sweetalert"
import PageLoader from "./../../assets/loading/pageLoad1.gif"
import { usePermissions } from "API/PermissionContext"

const CALC_TYPES = { 1: "Quantity Based", 2: "FAT & SNF", 3: "Fat Range", 4: "SNF Range", 5: "TS Range", 6: "Rate Percentage" }

const IncentiveCharts = () => {
  const { canAdd, canEdit, canDelete, MODULE } = usePermissions()
  const MID = MODULE.INCENTIVE_CHARTS

  const [incentives, setIncentives] = useState([])
  const [pageLoad, setPageLoad] = useState(true)
  const [active, setActive] = useState(true)
  const [searchTxt, setSearchTxt] = useState("")
  const [showFilter, setShowFilter] = useState(false)
  const [fName, setFName] = useState("")
  const [fCalc, setFCalc] = useState("")
  const [selected, setSelected] = useState([])

  // alerts
  const [confirm_alert, setconfirm_alert] = useState(false)
  const [success_dlg, setsuccess_dlg] = useState(false)
  const [dlgMsg, setDlgMsg] = useState("")

  // modal
  const [modalOpen, setModalOpen] = useState(false)
  const [editId, setEditId] = useState(null)
  const [saving, setSaving] = useState(false)

  // form fields
  const [chartName, setChartName] = useState("")
  const [calculationType, setCalculationType] = useState("")
  const [minQuantity, setMinQuantity] = useState("0")
  const [applyIncentive, setApplyIncentive] = useState("")
  const [basedOn, setBasedOn] = useState("1")
  const [incentivePerLtr, setIncentivePerLtr] = useState("")
  const [ratePercentageVal, setRatePercentageVal] = useState("")
  const [limitFatSnf, setLimitFatSnf] = useState("0")
  const [minFat, setMinFat] = useState("")
  const [maxFat, setMaxFat] = useState("")
  const [minSnf, setMinSnf] = useState("")
  const [maxSnf, setMaxSnf] = useState("")
  const [fatInc, setFatInc] = useState("0.1")
  const [snfInc, setSnfInc] = useState("0.1")
  const [rangeRows, setRangeRows] = useState([])
  const [fats, setFats] = useState([])
  const [snfs, setSnfs] = useState([])
  const [fatSnfData, setFatSnfData] = useState({})
  const [showGrid, setShowGrid] = useState(false)
  const [fileUpload, setFileUpload] = useState(null)

  useEffect(() => { loadList() }, [active])

  const loadList = async () => {
    setPageLoad(true)
    try {
      const url = active ? "active-incentive-charts" : "inactive-incentive-charts"
      const resp = await api.get(url)
      const d = resp.data?.data || resp.data || []
      setIncentives(Array.isArray(d) ? d : [])
    } catch (e) { setIncentives([]) }
    setPageLoad(false)
  }

  const safeParse = (val) => {
    if (!val) return null
    if (typeof val === "object") return val
    try { return JSON.parse(val) } catch { return null }
  }

  // filtered
  const filtered = useMemo(() => {
    let d = incentives
    if (searchTxt) { const s = searchTxt.toLowerCase(); d = d.filter(r => (r.chartName||"").toLowerCase().includes(s) || (CALC_TYPES[r.calculationType]||"").toLowerCase().includes(s)) }
    if (fName) d = d.filter(r => (r.chartName||"").toLowerCase().includes(fName.toLowerCase()))
    if (fCalc) d = d.filter(r => String(r.calculationType) === fCalc)
    return d
  }, [incentives, searchTxt, fName, fCalc])

  const allSel = filtered.length > 0 && filtered.every(r => selected.includes(r.id))
  const toggleAll = () => allSel ? setSelected([]) : setSelected(filtered.map(r => r.id))
  const toggleOne = id => setSelected(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id])

  const statusChange = async (id) => {
    try {
      await api.put("update-incentive-chart-status/" + id, { status: active ? 0 : 1 })
      setIncentives(p => p.filter(x => x.id !== id))
    } catch (e) { console.error(e) }
  }

  const resetForm = () => {
    setChartName(""); setCalculationType(""); setMinQuantity("0"); setApplyIncentive("")
    setBasedOn("1"); setIncentivePerLtr(""); setRatePercentageVal(""); setLimitFatSnf("0")
    setMinFat(""); setMaxFat(""); setMinSnf(""); setMaxSnf(""); setFatInc("0.1"); setSnfInc("0.1")
    setRangeRows([]); setFats([]); setSnfs([]); setFatSnfData({}); setShowGrid(false); setFileUpload(null)
  }

  const openAdd = () => { resetForm(); setEditId(null); setModalOpen(true) }

  const openEdit = async (id) => {
    resetForm()
    try {
      const resp = await api.get("incentive-chart/" + id)
      const d = resp.data?.data
      if (!d) return alert("Not found")
      setEditId(id)
      setChartName(d.chartName || "")
      setCalculationType(String(d.calculationType || ""))
      setMinQuantity(String(d.minQuantity || "0"))
      setApplyIncentive(d.applyIncentive || "")
      setBasedOn(String(d.basedOn || "1"))
      setIncentivePerLtr(d.incentivePerLtr || "")
      setRatePercentageVal(d.ratePercentage || "")
      setLimitFatSnf(String(d.limitFatSnf || "0"))
      setMinFat(d.minFat || ""); setMaxFat(d.maxFat || "")
      setMinSnf(d.minSnf || ""); setMaxSnf(d.maxSnf || "")

      const ct = String(d.calculationType)
      // FAT & SNF grid
      if (ct === "2") {
        const parsed = safeParse(d.fat_snf)
        if (parsed) setFatSnfData(parsed)
        if (d.minFat && d.maxFat && d.minSnf && d.maxSnf) {
          genGrid(parseFloat(d.minFat), parseFloat(d.maxFat), parseFloat(d.minSnf), parseFloat(d.maxSnf), 0.1, 0.1)
        }
      }
      // Fat Range rows
      if (ct === "3") { const p = safeParse(d.fatRange); if (Array.isArray(p)) setRangeRows(p) }
      // SNF Range rows
      if (ct === "4") { const p = safeParse(d.snfRange); if (Array.isArray(p)) setRangeRows(p) }
      // TS Range rows
      if (ct === "5") { const p = safeParse(d.snfRange); if (Array.isArray(p)) setRangeRows(p) }

      setModalOpen(true)
    } catch (e) { alert("Error loading record") }
  }

  // grid generation
  const genGrid = (mnF, mxF, mnS, mxS, fI, sI) => {
    if (isNaN(mnF)||isNaN(mxF)||isNaN(mnS)||isNaN(mxS)) return
    if (mnF >= mxF || mnS >= mxS) return alert("Min must be less than Max")
    const fatArr = [], snfArr = []
    for (let i = mnF; i <= mxF + 0.001; i += (fI||0.1)) fatArr.push(i.toFixed(2))
    for (let i = mnS; i <= mxS + 0.001; i += (sI||0.1)) snfArr.push(i.toFixed(2))
    if (fatArr.length > 200 || snfArr.length > 200) return alert("Grid too large (max 200x200)")
    setFats(fatArr); setSnfs(snfArr); setShowGrid(true)
  }

  const getCellVal = (f, s) => { try { if (fatSnfData[f] && fatSnfData[f][s] !== undefined) return fatSnfData[f][s] } catch(e){} return "" }
  const setCellVal = (f, s, v) => { setFatSnfData(p => ({ ...p, [f]: { ...(p[f]||{}), [s]: v } })) }

  const addRangeRow = () => setRangeRows(p => [...p, { from: "", upto: "", rate: "" }])
  const delRangeRow = (i) => setRangeRows(p => p.filter((_, idx) => idx !== i))
  const updateRangeRow = (i, k, v) => setRangeRows(p => { const n = [...p]; n[i] = { ...n[i], [k]: v }; return n })

  // CSV file handler - parses CSV and fills the FAT/SNF grid
  const handleFile = (e) => {
    const file = e.target.files[0]; if (!file) return
    if (!file.name.toLowerCase().endsWith(".csv")) { alert("Only CSV files allowed"); e.target.value=""; return }
    if (file.size > 5 * 1024 * 1024) { alert("Max 5MB"); e.target.value=""; return }
    const reader = new FileReader()
    reader.onload = (evt) => {
      const lines = evt.target.result.replace(/\r\n/g,"\n").replace(/\r/g,"\n").split("\n").filter(l=>l.trim())
      if (lines.length < 2) { alert("CSV must have header + data rows"); return }
      const headers = lines[0].split(",").map(h => h.trim())
      // headers[0] = "FAT/SNF", headers[1..n] = SNF values
      const snfVals = headers.slice(1)
      const parsed = {}
      const fatVals = []
      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(",").map(c => c.trim())
        const fatKey = cols[0]
        if (!fatKey) continue
        fatVals.push(fatKey)
        parsed[fatKey] = {}
        for (let j = 0; j < snfVals.length; j++) {
          parsed[fatKey][snfVals[j]] = cols[j + 1] || ""
        }
      }
      // Auto-fill range from CSV data
      if (fatVals.length > 0) {
        setMinFat(fatVals[0]); setMaxFat(fatVals[fatVals.length - 1])
      }
      if (snfVals.length > 0) {
        setMinSnf(snfVals[0]); setMaxSnf(snfVals[snfVals.length - 1])
      }
      setFatSnfData(parsed)
      setFats(fatVals)
      setSnfs(snfVals)
      setShowGrid(true)
    }
    reader.readAsText(file)
  }

  // save
  const handleSave = async () => {
    if (!chartName) return alert("Chart Name is required")
    if (!calculationType) return alert("Calculation Type is required")
    const cType = calculationType
    if (cType === "1" && !incentivePerLtr) return alert("Incentive Per Liter is required")
    if (cType === "6" && !ratePercentageVal) return alert("Rate Percentage is required")
    if (minQuantity === "1" && !applyIncentive) return alert("Apply Incentive threshold is required")

    setSaving(true)
    try {
      const payload = { chartName, calculationType: cType, minQuantity, status: active ? 1 : 0 }
      if (minQuantity === "1") { payload.applyIncentive = applyIncentive; payload.basedOn = basedOn }
      else { payload.applyIncentive = "0"; payload.basedOn = "0" }
      if (cType === "1") payload.incentivePerLtr = incentivePerLtr
      if (cType === "6") payload.ratePercentage = ratePercentageVal
      if (cType === "2") {
        payload.fat_snf = fatSnfData; payload.minFat = minFat; payload.maxFat = maxFat; payload.minSnf = minSnf; payload.maxSnf = maxSnf
      }
      if (cType === "3") payload.fatRange = rangeRows
      if (cType === "4") payload.rangeItems = rangeRows
      if (cType === "5") {
        payload.limitFatSnf = limitFatSnf; payload.rangeItems = rangeRows
        if (limitFatSnf === "1") { payload.minFat = minFat; payload.maxFat = maxFat; payload.minSnf = minSnf; payload.maxSnf = maxSnf }
      }

      if (editId) {
        await api.put("update-incentive-chart/" + editId, payload)
        setDlgMsg("Updated successfully")
      } else {
        payload.status = 1
        await api.post("create-incentive-chart", payload)
        setDlgMsg("Created successfully")
      }
      setModalOpen(false); setsuccess_dlg(true); loadList(); setSelected([])
    } catch (e) { alert("Error: " + (e.response?.data?.data || e.message)) }
    setSaving(false)
  }

  // delete
  const handleBulkDelete = async () => {
    if (!selected.length) return
    try {
      await api.post("bulk-delete-incentive-charts", { ids: selected })
      setSelected([]); setDlgMsg("Deleted successfully"); setsuccess_dlg(true); loadList()
    } catch (e) { alert("Delete failed") }
  }

  const ib = (bg, icon, title, onClick, disabled) => (
    <button title={title} onClick={onClick} disabled={disabled}
      style={{ width: 32, height: 32, borderRadius: 4, background: disabled ? "#ccc" : bg, color: "#fff", border: "none", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 14, cursor: disabled ? "not-allowed" : "pointer" }}>
      <i className={`fas ${icon}`} />
    </button>
  )

  // generate CSV template for FAT/SNF grid download
  const downloadTemplate = () => {
    const mnF = parseFloat(minFat), mxF = parseFloat(maxFat), mnS = parseFloat(minSnf), mxS = parseFloat(maxSnf)
    if (isNaN(mnF)||isNaN(mxF)||isNaN(mnS)||isNaN(mxS)||mnF>=mxF||mnS>=mxS) return alert("Enter valid FAT/SNF range first")
    const fi = parseFloat(fatInc)||0.1, si = parseFloat(snfInc)||0.1
    const fatArr = [], snfArr = []
    for (let i=mnF;i<=mxF+0.001;i+=fi) fatArr.push(i.toFixed(2))
    for (let i=mnS;i<=mxS+0.001;i+=si) snfArr.push(i.toFixed(2))
    let csv = "FAT/SNF," + snfArr.join(",") + "\n"
    fatArr.forEach(f => { csv += f + "," + snfArr.map(() => "").join(",") + "\n" })
    const blob = new Blob([csv], { type: "text/csv" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a"); a.href = url; a.download = "incentive_template.csv"; a.click(); URL.revokeObjectURL(url)
  }

  const ct = calculationType

  const switchTab = (isActive) => { setActive(isActive); setSelected([]); setSearchTxt(""); setShowFilter(false); setFName(""); setFCalc("") }

  return (
    <>
      {/* Toolbar: Tabs + Icons + Search in one row */}
      <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "6px", marginBottom: "10px", background: "#f8f9fa", borderRadius: "8px", padding: "4px 8px", border: "1px solid #dee2e6" }}>
        {/* Tabs */}
        {[
          { id: true, label: "Active Incentive Charts", icon: "fa-check-circle" },
          { id: false, label: "Inactive Incentive Charts", icon: "fa-ban" },
        ].map(t => (
          <button key={String(t.id)} onClick={() => switchTab(t.id)}
            style={{
              display: "flex", alignItems: "center", gap: "5px",
              padding: "5px 12px", border: "none", borderRadius: "6px", cursor: "pointer",
              fontWeight: 600, fontSize: "13px", whiteSpace: "nowrap",
              background: active === t.id ? "#0d6efd" : "transparent",
              color: active === t.id ? "#fff" : "#495057",
              transition: "background 0.15s, color 0.15s",
            }}>
            <i className={`fas ${t.icon}`} style={{ fontSize: "12px" }} />
            {t.label}
          </button>
        ))}

        {/* Divider */}
        <div style={{ width: 1, height: 24, background: "#dee2e6", margin: "0 2px" }} />

        {canAdd(MID) && ib("#28a745", "fa-plus", "Add Incentive Chart", openAdd, false)}
        {canDelete(MID) && ib("#dc3545", "fa-trash", selected.length > 0 ? `Delete ${selected.length}` : "Delete Selected", () => { if (selected.length) setconfirm_alert(true); else alert("Select records first") }, !selected.length)}
        {ib("#0d6efd", "fa-filter", "Toggle Filter", () => { setShowFilter(!showFilter); if (showFilter) { setFName(""); setFCalc("") } }, false)}
        <div style={{ flex: 1 }} />
        <div style={{ display: "flex", gap: 4 }}>
          <input className="form-control form-control-sm" style={{ width: 180, fontSize: 13 }} placeholder="Search" value={searchTxt} onChange={e => setSearchTxt(e.target.value)} />
          {ib("#0d6efd", "fa-search", "Search", null, false)}
        </div>
      </div>

      {/* Content */}
      {pageLoad ? <div className="pageLoader"><img src={PageLoader} alt="Loading..." /></div> : (
        <div style={{ overflowX: "auto" }}>
          <table className="table table-sm table-bordered table-hover mb-0 dairyTbl" style={{ fontSize: 13 }}>
            <thead style={{ background: "#f8f9fa" }}>
              <tr>
                <th style={{ width: 28 }}><input type="checkbox" checked={allSel} onChange={toggleAll} /></th>
                <th>#</th><th>Incentive Chart Name</th><th>Calculation Type</th><th>Status</th><th>Created</th><th>Action</th>
              </tr>
              {showFilter && (
                <tr style={{ background: "#f1f3f5" }}>
                  <td></td><td></td>
                  <td><input className="form-control form-control-sm" placeholder="Name" value={fName} onChange={e => setFName(e.target.value)} /></td>
                  <td><select className="form-control form-control-sm" value={fCalc} onChange={e => setFCalc(e.target.value)}><option value="">All</option>{Object.entries(CALC_TYPES).map(([k,v]) => <option key={k} value={k}>{v}</option>)}</select></td>
                  <td colSpan={2}></td>
                  <td><button className="btn btn-sm btn-outline-danger" onClick={() => { setFName(""); setFCalc("") }}><i className="fa fa-times" /></button></td>
                </tr>
              )}
            </thead>
            <tbody>
              {filtered.length > 0 ? filtered.map((r, i) => (
                <tr key={r.id}>
                  <td><input type="checkbox" checked={selected.includes(r.id)} onChange={() => toggleOne(r.id)} /></td>
                  <td>{i + 1}</td>
                  <td>{r.chartName}</td>
                  <td>{CALC_TYPES[r.calculationType] || r.calculationType}</td>
                  <td>
                    <div className="form-check form-switch form-switch-lg mb-0">
                      <input type="checkbox" className="form-check-input" checked={active} onChange={() => statusChange(r.id)} />
                    </div>
                  </td>
                  <td style={{ fontSize: 11, color: "#6c757d" }}>{r.created_at ? new Date(r.created_at).toLocaleDateString("en-IN") : "â€”"}</td>
                  <td>
                    <div style={{ display: "flex", gap: 4 }}>
                      {canEdit(MID) && <button style={{ width: 28, height: 28, borderRadius: 4, background: "#0d6efd", color: "#fff", border: "none", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12, cursor: "pointer" }} onClick={() => openEdit(r.id)}><i className="fas fa-edit" /></button>}
                    </div>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan={7}><center style={{ padding: 20, color: "#6c757d" }}>No {active ? "Active" : "Inactive"} Incentive Charts Found</center></td></tr>
              )}
            </tbody>
          </table>
          <div style={{ color: "#0d6efd", fontSize: 13, marginTop: 6 }}>Showing {filtered.length} of {incentives.length} entries</div>
        </div>
      )}

      {/* Add/Edit Modal */}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} size="xl" backdrop="static">
        <div style={{ padding: "20px 24px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <h5 style={{ margin: 0 }}>Incentive Charts <span style={{ color: "red", fontSize: 13 }}>[* required]</span></h5>
            <button type="button" className="btn-close" onClick={() => setModalOpen(false)} />
          </div>
          <h6 style={{ fontWeight: 600, marginBottom: 16, color: "#495057" }}>Incentive Charts Information</h6>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px 16px" }}>
            <div>
              <label style={{ fontWeight: 700, fontSize: 13 }}>Incentive Chart Name *</label>
              <input className="form-control form-control-sm" value={chartName} onChange={e => setChartName(e.target.value)} />
            </div>
            <div>
              <label style={{ fontWeight: 700, fontSize: 13 }}>Calculation Type *</label>
              <select className="form-control form-control-sm" value={calculationType} onChange={e => { setCalculationType(e.target.value); setRangeRows([]); setShowGrid(false) }}>
                <option value="">Select</option>
                {Object.entries(CALC_TYPES).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontWeight: 700, fontSize: 13 }}>Minimum Quantity Required</label>
              <div style={{ display: "flex", gap: 16, marginTop: 4 }}>
                <label style={{ fontSize: 13 }}><input type="radio" name="minQty" checked={minQuantity === "1"} onChange={() => setMinQuantity("1")} /> Yes</label>
                <label style={{ fontSize: 13 }}><input type="radio" name="minQty" checked={minQuantity === "0"} onChange={() => setMinQuantity("0")} /> No</label>
              </div>
            </div>
          </div>

          {/* Min Quantity conditional */}
          {minQuantity === "1" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px 16px", marginTop: 12 }}>
              <div>
                <label style={{ fontWeight: 700, fontSize: 13 }}>Apply Incentive if Liters Greater Than *</label>
                <input className="form-control form-control-sm" value={applyIncentive} onChange={e => setApplyIncentive(e.target.value)} />
              </div>
              <div>
                <label style={{ fontWeight: 700, fontSize: 13 }}>Apply Liters Based On</label>
                <div style={{ display: "flex", gap: 16, marginTop: 4 }}>
                  <label style={{ fontSize: 13 }}><input type="radio" name="basedOn" checked={basedOn === "1"} onChange={() => setBasedOn("1")} /> Shiftwise</label>
                  <label style={{ fontSize: 13 }}><input type="radio" name="basedOn" checked={basedOn === "2"} onChange={() => setBasedOn("2")} /> Daywise</label>
                </div>
              </div>
            </div>
          )}

          {/* Type 1: Quantity Based */}
          {ct === "1" && (
            <div style={{ marginTop: 12, maxWidth: 300 }}>
              <label style={{ fontWeight: 700, fontSize: 13 }}>Incentive Per Liter *</label>
              <input className="form-control form-control-sm" value={incentivePerLtr} onChange={e => setIncentivePerLtr(e.target.value)} />
            </div>
          )}

          {/* Type 6: Rate Percentage */}
          {ct === "6" && (
            <div style={{ marginTop: 12, maxWidth: 300 }}>
              <label style={{ fontWeight: 700, fontSize: 13 }}>Rate Percentage *</label>
              <input className="form-control form-control-sm" value={ratePercentageVal} onChange={e => setRatePercentageVal(e.target.value)} />
            </div>
          )}

          {/* Type 2: FAT & SNF */}
          {ct === "2" && (
            <div style={{ marginTop: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
                <label style={{ fontWeight: 700, fontSize: 13, minWidth: 80 }}>FAT Range</label>
                <input className="form-control form-control-sm" style={{ width: 120 }} placeholder="Min" value={minFat} onChange={e => setMinFat(e.target.value)} />
                <input className="form-control form-control-sm" style={{ width: 120 }} placeholder="Max" value={maxFat} onChange={e => setMaxFat(e.target.value)} />
                <label style={{ fontWeight: 600, fontSize: 13 }}>Increment</label>
                <input className="form-control form-control-sm" style={{ width: 80, background: "#f8f9fa" }} value={fatInc} readOnly />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                <label style={{ fontWeight: 700, fontSize: 13, minWidth: 80 }}>SNF Range</label>
                <input className="form-control form-control-sm" style={{ width: 120 }} placeholder="Min" value={minSnf} onChange={e => setMinSnf(e.target.value)} />
                <input className="form-control form-control-sm" style={{ width: 120 }} placeholder="Max" value={maxSnf} onChange={e => setMaxSnf(e.target.value)} />
                <label style={{ fontWeight: 600, fontSize: 13 }}>Increment</label>
                <input className="form-control form-control-sm" style={{ width: 80, background: "#f8f9fa" }} value={snfInc} readOnly />
              </div>
              <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                <button className="btn btn-success btn-sm" type="button" onClick={() => genGrid(parseFloat(minFat), parseFloat(maxFat), parseFloat(minSnf), parseFloat(maxSnf), parseFloat(fatInc), parseFloat(snfInc))}>Generate</button>
                <button className="btn btn-info btn-sm" type="button" onClick={downloadTemplate} title="Download CSV template with FAT/SNF grid"><i className="fas fa-download" style={{ marginRight: 4 }} />Download Template</button>
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontWeight: 700, fontSize: 13 }}>Upload CSV</label>
                <input type="file" className="form-control form-control-sm" style={{ maxWidth: 350 }} accept=".csv" onChange={handleFile} />
                <small style={{ color: "#6c757d" }}>Upload filled CSV template to populate grid values</small>
              </div>

              {showGrid && fats.length > 0 && (
                <div className="table-responsive mt-2" style={{ maxHeight: 400, overflow: "auto" }}>
                  <table className="table table-bordered table-sm" style={{ fontSize: 11 }}>
                    <thead><tr style={{ position: "sticky", top: 0, background: "#f8f9fa", zIndex: 2 }}>
                      <th style={{ position: "sticky", left: 0, background: "#e9ecef", zIndex: 3 }}>FAT/SNF</th>
                      {snfs.map(s => <th key={s} style={{ minWidth: 60, textAlign: "center" }}>{s}</th>)}
                    </tr></thead>
                    <tbody>{fats.map(f => (
                      <tr key={f}>
                        <th style={{ position: "sticky", left: 0, background: "#e9ecef", zIndex: 1 }}>{f}</th>
                        {snfs.map(s => <td key={s} style={{ padding: 1 }}><input className="form-control form-control-sm" style={{ minWidth: 50, fontSize: 10, padding: "1px 3px" }} value={getCellVal(f, s)} onChange={e => setCellVal(f, s, e.target.value)} /></td>)}
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Type 3: Fat Range / Type 4: SNF Range / Type 5: TS Range - dynamic rows */}
          {(ct === "3" || ct === "4" || ct === "5") && (
            <div style={{ marginTop: 16 }}>
              {ct === "5" && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "12px 16px", marginBottom: 12 }}>
                  <div>
                    <label style={{ fontWeight: 700, fontSize: 13 }}>Limit Min/Max FAT, SNF?</label>
                    <div style={{ display: "flex", gap: 16, marginTop: 4 }}>
                      <label style={{ fontSize: 13 }}><input type="radio" name="limitFS" checked={limitFatSnf === "1"} onChange={() => setLimitFatSnf("1")} /> Yes</label>
                      <label style={{ fontSize: 13 }}><input type="radio" name="limitFS" checked={limitFatSnf === "0"} onChange={() => setLimitFatSnf("0")} /> No</label>
                    </div>
                  </div>
                  {limitFatSnf === "1" && (
                    <>
                      <div><label style={{ fontWeight: 700, fontSize: 13 }}>Min FAT</label><input className="form-control form-control-sm" value={minFat} onChange={e => setMinFat(e.target.value)} /></div>
                      <div><label style={{ fontWeight: 700, fontSize: 13 }}>Max FAT</label><input className="form-control form-control-sm" value={maxFat} onChange={e => setMaxFat(e.target.value)} /></div>
                      <div><label style={{ fontWeight: 700, fontSize: 13 }}>Min SNF</label><input className="form-control form-control-sm" value={minSnf} onChange={e => setMinSnf(e.target.value)} /></div>
                    </>
                  )}
                  {limitFatSnf === "1" && (
                    <div><label style={{ fontWeight: 700, fontSize: 13 }}>Max SNF</label><input className="form-control form-control-sm" value={maxSnf} onChange={e => setMaxSnf(e.target.value)} /></div>
                  )}
                </div>
              )}
              <h6 style={{ fontWeight: 600 }}>{ct === "3" ? "FAT" : ct === "4" ? "SNF" : "TS"} Range Items</h6>
              <table className="table table-sm table-bordered" style={{ fontSize: 13 }}>
                <thead><tr>
                  <th>S.No</th>
                  <th>{ct === "3" ? "FAT" : ct === "4" ? "SNF" : "TS"} Greater Than</th>
                  <th>{ct === "3" ? "FAT" : ct === "4" ? "SNF" : "TS"} Upto</th>
                  <th>Rate</th>
                  <th><button type="button" className="btn btn-primary btn-sm" onClick={addRangeRow}><i className="fa fa-plus" /></button></th>
                </tr></thead>
                <tbody>
                  {rangeRows.map((r, i) => (
                    <tr key={i}>
                      <td>{i + 1}</td>
                      <td><input className="form-control form-control-sm" value={r.from || ""} onChange={e => updateRangeRow(i, "from", e.target.value)} /></td>
                      <td><input className="form-control form-control-sm" value={r.upto || ""} onChange={e => updateRangeRow(i, "upto", e.target.value)} /></td>
                      <td><input className="form-control form-control-sm" value={r.rate || ""} onChange={e => updateRangeRow(i, "rate", e.target.value)} /></td>
                      <td><i className="fas fa-trash-alt" style={{ cursor: "pointer", color: "#dc3545" }} onClick={() => delRangeRow(i)} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Modal Footer */}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20 }}>
            <button className="btn btn-success btn-sm" style={{ fontWeight: 700 }} onClick={handleSave} disabled={saving}>{saving ? "Saving..." : "Save Changes"}</button>
            <button className="btn btn-danger btn-sm" onClick={() => setModalOpen(false)}>Close</button>
          </div>
        </div>
      </Modal>

      {/* Alerts */}
      {confirm_alert && <SweetAlert title="Delete selected charts?" warning showCancel confirmBtnBsStyle="success" cancelBtnBsStyle="danger"
        onConfirm={() => { handleBulkDelete(); setconfirm_alert(false) }}
        onCancel={() => setconfirm_alert(false)}>You won't be able to revert this!</SweetAlert>}
      {success_dlg && <SweetAlert success title="Done!" onConfirm={() => { setsuccess_dlg(false); setDlgMsg("") }}>{dlgMsg || "Operation completed."}</SweetAlert>}
    </>
  )
}

export default IncentiveCharts

